import { parseDate } from "chrono-node";
import _ from "lodash";
import moment from "moment";
import React from "react";
import escapeName from "../../utils/escape-form-name";

const rome = process.browser && require("rome");

import { DATE_FORMATS } from "../../constants";
import { RillContext } from "../../rill/RillReact/share/base";

const { DATE_PICKER_FORMAT } = DATE_FORMATS;

const ROME_TODAY_CLASS = "rd-day-today";

class DateInput extends React.Component {
  constructor(props) {
    super(props);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.positionCalendar = this.positionCalendar.bind(this);
    this.markTodayInCalendar = this.markTodayInCalendar.bind(this);
    this.textInput = null;
    this.setTextInputRef = (element) => {
      this.textInput = element;
    };
  }

  toDatePickerFormat(date) {
    if (!date) return "";
    if (typeof date === "string") {
      return this.toDatePickerFormat(parseDate(date));
    }
    if (date instanceof Date && Number.isFinite(date.getTime())) {
      date = moment(date);
    }
    if (typeof date.format === "function" && date.isValid()) {
      const startOfTime = moment(0);
      if (date.isSameOrBefore(startOfTime)) return "";
      return date.format(DATE_PICKER_FORMAT);
    }

    return "";
  }

  /**
   * Rome does not tag “today” on day cells. Walk each month, parse the label,
   * and add {@link ROME_TODAY_CLASS} to the cell that matches local today.
   */
  markTodayInCalendar() {
    if (!this.props.highlightTodayInCalendar || !this.rome?.container) return;

    const container = this.rome.container;
    const monthFmt = this.props.monthFormat || "MMMM YYYY";
    const today = moment().startOf("day");

    container.querySelectorAll("td.rd-day-body").forEach((td) => {
      td.classList.remove(ROME_TODAY_CLASS);
    });

    container.querySelectorAll(".rd-month").forEach((monthEl) => {
      const labelEl = monthEl.querySelector(".rd-month-label");
      if (!labelEl) return;

      const monthStart = moment(labelEl.textContent.trim(), monthFmt, true);
      if (!monthStart.isValid()) return;

      monthEl.querySelectorAll("td.rd-day-body").forEach((td) => {
        const dayNum = parseInt(td.textContent.trim(), 10);
        if (Number.isNaN(dayNum)) return;

        let cellDate;
        if (td.classList.contains("rd-day-prev-month")) {
          cellDate = monthStart.clone().subtract(1, "month").date(dayNum);
        } else if (td.classList.contains("rd-day-next-month")) {
          cellDate = monthStart.clone().add(1, "month").date(dayNum);
        } else {
          cellDate = monthStart.clone().date(dayNum);
        }

        if (cellDate.isValid() && cellDate.isSame(today, "day")) {
          td.classList.add(ROME_TODAY_CLASS);
        }
      });
    });
  }

  scheduleMarkTodayInCalendar() {
    if (!this.props.highlightTodayInCalendar) return;
    setTimeout(() => {
      this.markTodayInCalendar();
    }, 0);
  }

  setOptions() {
    const weekStart = this.context.locals.activeUser.startingCalendarDay || 0;
    const opts = { ...this.props, weekStart };
    if (this.rome) {
      this.rome.options(opts);
    } else {
      const prefs = Object.assign(_.clone(this.props), {
        weekStart,
        styles: {
          container: _.get(this.props, "altPosition", false)
            ? "rd-container alt-position"
            : "rd-container",
        },
      });

      this.rome = rome(this.textInput, prefs);
      // Only on first creation - this.date is the last CONFIRMED value (set
      // either here, from the initial prop-driven value, or by an actual day
      // click below). Re-running this on every componentDidUpdate (as it did
      // before) let it pick up whatever rome was showing mid-interaction -
      // e.g. a month the user had merely navigated to without picking a day -
      // corrupting the one field this component uses to tell "confirmed" from
      // "just being browsed" apart (sc-47729).
      this.date = this.rome.getDateString();
      this.confirmedText = this.textInput.value;

      if (this.props.highlightTodayInCalendar && !this._romeTodayListenersAttached) {
        this._romeTodayListenersAttached = true;
        this.rome.on("show", this.scheduleMarkTodayInCalendar);
        this.rome.on("next", this.scheduleMarkTodayInCalendar);
        this.rome.on("back", this.scheduleMarkTodayInCalendar);
      }
    }

    const romeContainer = this.rome.associated.getBoundingClientRect();
    if (this.props.alignRight) {
      const rightFromLeft = romeContainer.right;
      this.rome.container.classList.add(`align-calendar-right`);
      this.rome.container.style.right = `calc(100vw - ${rightFromLeft}px)`;
    }

    // rome.options() calls api.off() internally, wiping all rome.on listeners.
    // Re-attach on every setOptions call so they survive componentDidUpdate cycles.
    this.rome.on("show", this.positionCalendar);

    // DOM click listener survives rome.options() since the container element is
    // reused. Only attach once via a separate guard.
    if (!this.containerListenerAttached) {
      this.containerListenerAttached = true;
      // Capture phase: fires before rome's bubbling pickDay handler so
      // dayClicked is set before the "data" event emits.
      this.rome.container.addEventListener(
        "click",
        (e) => {
          const t = e.target;
          if (
            t.classList.contains("rd-day-body") &&
            !t.classList.contains("rd-day-disabled")
          ) {
            this.dayClicked = true;
          }
        },
        true
      );
    }

    this.rome.on("data", (data) => {
      if (!this.dayClicked) return;
      this.dayClicked = false;
      if (this.date !== data) {
        this.date = data;
        this.confirmedText = data;
        this.props.onChange(data);
      }
    });

    // rome redraws the visible textbox as the user browses months even when
    // no day was actually clicked (dayClicked/the "data" handler above never
    // fired, so this.date - the last CONFIRMED value - is untouched). Without
    // this, closing the picker after only navigating months left the field
    // showing a date that was never passed to onChange, silently out of sync
    // with the real form value Save would submit (sc-47729 - "Add Event"'s
    // date field showed the navigated-to month/day, but Save stored the
    // previously clicked one). Revert the display back to the confirmed
    // value instead of adopting whatever rome happened to be showing.
    this.rome.on("hide", () => {
      // rome.setValue is silent (it never rewrites the textbox), and an empty
      // field has no this.date to restore, so the text is reverted explicitly
      // from confirmedText.
      if (this.rome.getDateString() !== this.date) {
        this.rome.setValue(this.date);
      }
      if (this.textInput.value !== this.confirmedText) {
        this.textInput.value = this.confirmedText;
      }
    });
  }

  handleKeyDown(e) {
    const { keyCode } = e;
    if (keyCode === 13) {
      this.handleInputBlur(e);
    }
  }

  handleInputBlur({ target }) {
    if (target.value !== this.rome.getDateString()) {
      this.rome.setValue(this.toDatePickerFormat(target.value));
      this.date = this.rome.getDateString();
    }
    this.confirmedText = target.value;
  }
  positionCalendar() {
    const romeContainer = this.rome?.container.getBoundingClientRect();
    const associated = this.rome?.associated.getBoundingClientRect();

    if (!romeContainer) return;

    const overFlowBottom = romeContainer.bottom - window.innerHeight;

    if (overFlowBottom > 0) {
      this.rome.container.classList.add(`position-top`);
      // `bottom` is measured from the document, so the page scroll counts too.
      this.rome.container.style.bottom = `calc(100vh - ${associated.top + window.scrollY}px + 0.5rem)`;
    }
  }

  componentDidMount() {
    this.setOptions();
    window.addEventListener("resize", this.positionCalendar);
  }
  componentDidUpdate() {
    this.setOptions();
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.positionCalendar);
    _.invoke(this.rome, "destroy");
  }
  UNSAFE_componentWillReceiveProps(next) {
    const prevValue = this.toDatePickerFormat(this.props.value);
    const nextValue = this.toDatePickerFormat(next.value);
    if (prevValue !== nextValue) {
      this.textInput.value = nextValue;
      this.handleInputBlur({ target: this.textInput });
    }
  }

  render() {
    const {
      name,
      customClassNames = "",
      datePlaceholder,
      isDisabled = false,
    } = this.props;
    const currentValue = this.props.value || this.props.defaultValue || "";
    const defaultValue = this.toDatePickerFormat(currentValue);

    return (
      <div
        role="date-selector-wrapper"
        style={this.props.smallSize ? { width: "145px" } : {}}
      >
        <input
          key={name}
          // ref="input"
          ref={this.setTextInputRef}
          type="text"
          style={
            this.props.dontPadding
              ? { marginBottom: "0px" }
              : this.props.smallSize
                ? { color: "#B9B9B9" }
                : {}
          }
          className={`${customClassNames} text-input`}
          autoComplete="off"
          readOnly
          onBlur={this.handleInputBlur}
          onKeyDown={this.handleKeyDown}
          {..._.omit(
            this.props,
            _.keys(DateInput.defaultProps).concat(
              "value",
              "max",
              "min",
              "dateValidator",
              "highlightTodayInCalendar",
              // React-only / wrapper props — must not reach the native <input>
              "customClassNames",
              "datePlaceholder",
              "smallSize",
              "dontPadding",
              "isDisabled",
              "forceOnChange",
              "alignRight"
            )
          )}
          onChange={this.props.forceOnChange ? this.props.onChange : _.noop}
          defaultValue={defaultValue}
          placeholder={datePlaceholder || "Select Date"}
          disabled={isDisabled}
        />

        <input type="hidden" name="_dates[]" value={escapeName(name)} />
        <img src={"/img/svg/angle-down.svg"} alt="open" />
      </div>
    );
  }
}

DateInput.defaultProps = {
  altPosition: false,
  autoClose: true,
  autoHideOnBlur: true,
  autoHideOnClick: true,
  date: true,
  time: false,
  dayFormat: "D",
  monthFormat: "MMMM YYYY",
  inputFormat: DATE_PICKER_FORMAT,
  //   weekdayFormat: ["S", "M", "T", "W", "T", "F", "S"],
  weekdayFormat: "short",
  monthsInCalendar: 1,
  dontPadding: false,
  highlightTodayInCalendar: false,
  onChange: _.noop,
};

DateInput.contextType = RillContext;

export default DateInput;
